import React, { useState } from 'react'
import { useStatusOverlay } from '@fs/zion-ui'
import { useEffectiveUser } from '@fs/zion-user'
import { AamInitialState, aamReducer } from './AamReducer'
import { deconstructName, formatName } from '../../../shared/helpers/name_formatting'
import { useTileData, useAncestorYearMatch } from '../hooks/hooks'
import { useDetailedFamilyTreeData } from '../../../shared/hooks/useDetailedFamilyTreeData'
import usePersonDetails from '../../../shared/hooks/usePersonDetails'
import setCookie from '../../../shared/helpers/cookie'
import { extractFourDigitYear } from '../../../shared/helpers/factDate_Helper'
import { useDSCState } from '../../../state/DSCStore'

import countryDetails from '../../../shared/data/countryDetails.json'

const AamStateContext = React.createContext()
const AamDispatchContext = React.createContext()

function AamProvider({ children }) {
  const [state, dispatch] = React.useReducer(aamReducer, AamInitialState)
  return (
    <AamStateContext.Provider value={state}>
      <AamDispatchContext.Provider value={dispatch}>{children}</AamDispatchContext.Provider>
    </AamStateContext.Provider>
  )
}

function useAamState() {
  const context = React.useContext(AamStateContext)
  if (context === undefined) {
    throw new Error('ERROR: useAamState must be used inside of AamProvider')
  }
  return context
}

function useAamDispatch() {
  const context = React.useContext(AamDispatchContext)
  if (context === undefined) {
    throw new Error('ERROR: useAamDispatch must be used inside of AamProvider')
  }
  return context
}

const nameTiles = [4, 27, 28]
export const expandTileData = ({ tileData, firstName, lastName, year, personId, gender, atn }) => {
  return tileData.map((item) => {
    const fullName = `${firstName} ${lastName}`
    const formattedName = formatName(fullName, item)
    item.parsedNames = deconstructName(formattedName)
    item.personId = personId
    item.gender = gender
    item.atn = atn
    item.needsDOB = !nameTiles.includes(item.id)
    if (item.type !== 'ancestorDetails') {
      item.header = item.header.replace('{{NAME}}', formattedName)
    }
    if (item.type === 'globalNameCounts' || item.type === 'nameHeatmap') {
      item.header = formattedName
    }
    if (year) {
      item.year = year
    }
    return item
  })
}

function useAamInitialization(user) {
  // use this hook only once to load required application data on page load
  const dispatch = useAamDispatch()
  const dscState = useDSCState()

  const [treeDataState] = useDetailedFamilyTreeData(5)
  const [personDetailsState] = usePersonDetails(user.personId)
  const [tileDataState, getTileData] = useTileData()
  const ancestorTiles = useAncestorYearMatch(treeDataState, personDetailsState, tileDataState, user.personId)
  const showStatusOverlay = useStatusOverlay()
  const [hasRequested, setHasRequested] = useState(false)

  React.useEffect(() => {
    if (personDetailsState.complete && !user?.userLoading) {
      const countryDetail = countryDetails.countries.find((item) => {
        return item.alpha2 === dscState?.customCountry ?? 'USA'
      })
      if (!hasRequested && !tileDataState.pending && !tileDataState.complete) {
        getTileData({
          countryCode: countryDetail?.alpha2 ?? 'us',
          year: extractFourDigitYear(personDetailsState.res.birthDate),
        })
        setHasRequested(true)
      }
    } else if (!user.userLoading) {
      // console("User isn't signed in")
    }
  }, [
    dscState.customCountry,
    getTileData,
    hasRequested,
    personDetailsState,
    tileDataState.complete,
    tileDataState.pending,
    user,
  ])

  React.useEffect(() => {
    if (tileDataState.complete && tileDataState.res && treeDataState.complete) {
      const treeData = treeDataState.res.persons
      const { givenName, familyName, personId, gender } = personDetailsState.res
      if (ancestorTiles && ancestorTiles.length !== 0) {
        ancestorTiles.forEach((ancestor) => tileDataState.res.push(ancestor))
      }

      const revisedTileData = expandTileData({
        tileData: tileDataState.res,
        firstName: givenName,
        lastName: familyName,
        personId,
        gender,
      })

      dispatch({
        type: 'INIT_AAM',
        data: {
          tree: treeData,
          tileData: revisedTileData,
          year: extractFourDigitYear(personDetailsState.res.birthDate),
          firstName: givenName,
          lastName: familyName,
          countryCode: personDetailsState?.res?.countryCode ?? 'us',
          isGuestUser: false,
        },
      })
    } else if (tileDataState.complete && !tileDataState.res) {
      showStatusOverlay({
        message: 'No data found!',
        type: 'error',
      })
    }
  }, [
    ancestorTiles,
    dispatch,
    personDetailsState,
    showStatusOverlay,
    tileDataState,
    tileDataState.complete,
    tileDataState.res,
    treeDataState.complete,
    treeDataState.res,
    user.personId,
  ])
}
// TO DO: Reduce duplication in these two hooks
function useAamGuestInitialization(nonstandardUser, appState) {
  const dispatch = useAamDispatch()
  const user = useEffectiveUser()
  const dscState = useDSCState()
  const [tileDataState, getTileData] = useTileData()
  const [hasRequested, setHasRequested] = useState(false)

  const languageRegion = nonstandardUser?.languageRegion?.value.split('_') ?? ['us', 'en']
  const showStatusOverlay = useStatusOverlay()

  setCookie('fslanguage', languageRegion[1])
  const region = languageRegion[0]

  React.useEffect(() => {
    if (
      !hasRequested &&
      !user.userLoading &&
      !appState.initialTileDataRequested &&
      nonstandardUser.initialized &&
      !nonstandardUser.authenticated &&
      nonstandardUser?.birthYear &&
      !tileDataState.pending &&
      !tileDataState.complete
    ) {
      getTileData({
        countryCode: region,
        year: parseInt(nonstandardUser.birthYear, 10),
      })
      setHasRequested(true)
    }
  }, [
    appState.initialTileDataRequested,
    getTileData,
    hasRequested,
    nonstandardUser.authenticated,
    nonstandardUser.birthYear,
    nonstandardUser.initialized,
    region,
    tileDataState.complete,
    tileDataState.pending,
    user.userLoading,
  ])

  React.useEffect(() => {
    if (tileDataState.complete && tileDataState.res) {
      const { firstName, lastName } = nonstandardUser
      const revisedTileData = expandTileData({
        tileData: tileDataState.res,
        firstName,
        lastName,
      })

      dispatch({
        type: 'INIT_AAM',
        data: {
          tileData: revisedTileData,
          year: extractFourDigitYear(nonstandardUser.birthYear),
          firstName,
          lastName,
          isGuestUser: true,
          guestUser: dscState?.nonstandardUser,
        },
      })
    } else if (tileDataState.complete && !tileDataState.res) {
      showStatusOverlay({
        message: 'No data found!',
        type: 'error',
      })
    }
  }, [
    dispatch,
    showStatusOverlay,
    tileDataState.complete,
    tileDataState.res,
    nonstandardUser,
    dscState.nonstandardUser,
  ])
}

export { AamProvider, useAamInitialization, useAamGuestInitialization, useAamState, useAamDispatch }
